import React, {
  useState,
  ChangeEvent,
  useEffect,
  useRef,
  MutableRefObject,
} from "react";
import { styled } from "styled-components";
import SearchInput from "./SearchInput";
import History from "./search-by-data/history/History";
import {
  isEmpty,
  scrollBarStyles,
  useAppLayout,
  useDebounce,
  useMountedEffect,
} from "@dataone/lib";
import SearchSuggestions from "./search-by-data/search-suggestions/SearchSuggestions";
import SearchResponse from "./search-by-data/search-response/SearchResponse";
import Databases from "./search-by-data/database/Databases";
import RecentSearches from "./search-by-data/search-response/recent-searches/RecentSearches";
import AskAbout from "./search-by-data/search-response/ask-about/AskAbout";
import SavedSearches from "./search-by-data/saved-searches/SavedSearches";
import { useSmartSearch } from "../../contexts/smart-search.provider";

interface SearchProps {
  stepRef2: MutableRefObject<null>;
  stepRef3: MutableRefObject<null>;
  stepRef4: MutableRefObject<null>;
  showResponse: boolean;
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setShowDocumentsViewer: (value: boolean) => void;
  setDocQuestion: (value: string) => void;
  setShowResponse: React.Dispatch<React.SetStateAction<boolean>>;
}

function Search({
  stepRef2,
  stepRef3,
  stepRef4,
  showResponse,
  isOpen,
  setOpen,
  setShowResponse,
  setShowDocumentsViewer,
  setDocQuestion,
}: SearchProps) {
  const [showComponents, setShowComponents] = useState(false);
  const [hasStopTyping, setHasStopTyping] = useState(false);
  const [isSearchLoading, setSearchLoading] = useState(false);
  const [searchApp, setSearchApp] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [enterPressCount, setEnterPressCount] = useState(0);

  const [selectedSearch, setSelectedSearch] = useState<{
    message: string;
  }>({ message: "" });

  const { response, selectedDB, setSelectedDB, setResponse } = useSmartSearch();

  const debouncedSearch = useDebounce(searchApp);

  const { isSmartSearchVisible } = useAppLayout();

  useEffect(() => {
    if (!showResponse) {
      if (response.id) {
        useAppLayout.setState({ isResponseVisible: true });
        setShowResponse(true);
      } else {
        setSelectedDB(null);
      }
      setSelectedSearch(() => ({
        message: "",
      }));
      setSearchApp(() => "");
      setSearchValue(() => "");
    }
  }, [showResponse]);

  useEffect(() => {
    if (!isSmartSearchVisible) {
      useAppLayout.setState({ isResponseVisible: false });
      setTimeout(() => {
        useAppLayout.setState({ isResponseVisible: true });
      }, 1000);
    }
  }, [isSmartSearchVisible]);

  const handleConfirmSearch = (value: string) => {
    if (!isEmpty(debouncedSearch) || !isEmpty(value)) {
      if (activeBtn === "searchByDatasets") {
        setSearchValue(value);
        useAppLayout.setState({ isResponseVisible: true });
        setShowResponse(true);
        setShowComponents(false);
        setEnterPressCount((prevCount) => prevCount + 1);
      } else if (activeBtn === "searchByDocuments") {
        setDocQuestion(value);
        setShowDocumentsViewer(true);
      }
    }
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchLoading(true);
    setSearchApp(value);
    if (value === debouncedSearch) {
      setSearchLoading(false);
    }
  };

  const handleInputFocus = () => {
    setShowComponents(true);
  };

  useMountedEffect(() => {
    if (!isEmpty(debouncedSearch)) setHasStopTyping(true);
    else setHasStopTyping(false);
    setSearchLoading(false);
  }, [debouncedSearch]);

  const [activeBtn, setActiveBtn] = useState<string>("searchByDatasets");
  // const handleActiveBtnChange = (btn: string) => {
  //   setActiveBtn(btn);
  // };

  const componentRef = useRef<HTMLDivElement | null>(null);
  const onBlurClick = (event: MouseEvent) => {
    const clickedElement = event.target as HTMLElement;
    if (
      componentRef.current &&
      !componentRef.current.contains(clickedElement) &&
      clickedElement?.offsetParent?.id === "smartsearch-modal"
    ) {
      setShowComponents(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", onBlurClick);
    return () => {
      document.removeEventListener("mousedown", onBlurClick);
    };
  }, []);

  // useEffect(() => {
  //   if (dropdownVisible) {
  //     setIsSavedDocDivHovered(true);
  //   }
  // }, [dropdownVisible]);
  const searchRef = useRef<HTMLDivElement>(null);
  // useEffect(() => {
  //   if (containerRef.current && showResponse) {
  //     containerRef.current.scrollTop = 0;
  //   }
  // }, [showResponse, containerRef.current]);

  return (
    <StyledContainer $showResponse={showResponse} ref={searchRef}>
      <div className="search-input" onBlur={() => onBlurClick}>
        <SearchInput
          stepRef2={stepRef2}
          inputValue={selectedSearch}
          handleSearchChange={handleSearchChange}
          handleInputFocus={handleInputFocus}
          isSearchLoading={isSearchLoading}
          showComponents={showComponents}
          showResponse={showResponse}
          // placeholder={
          //   activeBtn === "searchByDocuments" && !selectedDocLabel
          //     ? "Please Select Document first"
          //     : placeholders[activePlaceholderIndex]
          // }
          placeholder="Please select your dataset first"
          handleConfirmValue={handleConfirmSearch}
          disabled={activeBtn === "searchByDatasets" && !selectedDB}
        />
      </div>
      {/* {!showComponents && !showResponse && (
        <div>
          <StyledSearchType $isSavedDocDivHovered={isSavedDocDivHovered}>
            <SearchType
              activeBtn={activeBtn}
              onActiveBtnChange={handleActiveBtnChange}
            />
          </StyledSearchType>
        </div>
      )} */}
      <StyledFocusSelection $showResponse={showResponse} ref={componentRef}>
        {activeBtn !== "searchByDocuments" && showComponents && (
          <StyledSearchContent $showResponse={showResponse}>
            {!showResponse && hasStopTyping ? (
              <>
                {debouncedSearch && selectedDB?.id && (
                  <SearchSuggestions
                    databaseId={selectedDB.id}
                    search={debouncedSearch}
                    setSelectedSearch={setSelectedSearch}
                  />
                )}
              </>
            ) : !showResponse ? (
              <>
                <SavedSearches setSelectedSearch={setSelectedSearch} />
                <History setSelectedSearch={setSelectedSearch} />
              </>
            ) : debouncedSearch && selectedDB?.id ? (
              <>
                {debouncedSearch && selectedDB?.id && (
                  <SearchSuggestions
                    databaseId={selectedDB?.id}
                    search={debouncedSearch}
                    setSelectedSearch={setSelectedSearch}
                  />
                )}
              </>
            ) : (
              <>
                <RecentSearches
                  setSelectedSearch={setSelectedSearch}
                  history={response.recentSearches}
                />
                <AskAbout
                  setSelectedSearch={setSelectedSearch}
                  suggestion={response.suggestion}
                />
              </>
            )}
          </StyledSearchContent>
        )}
      </StyledFocusSelection>
      {activeBtn === "searchByDatasets" &&
        (showResponse ? (
          <SearchResponse
            searchRef={searchRef}
            selectedDB={selectedDB}
            setResponse={setResponse}
            response={response}
            selectedDBId={selectedDB?.id}
            searchApp={searchValue}
            setSelectedDB={setSelectedDB}
            setSelectedSearch={setSelectedSearch}
            enterPressCount={enterPressCount}
          />
        ) : (
          <StyledDataBases
          // onMouseEnter={() => setIsSavedDocDivHovered(true)}
          // onMouseLeave={() => setIsSavedDocDivHovered(false)}
          >
            <Databases
              selectedDB={selectedDB}
              stepRef3={stepRef3}
              stepRef4={stepRef4}
              isOpen={isOpen}
              setOpen={setOpen}
              setSelectedDB={setSelectedDB}
              setResponse={setResponse}
              setShowResponse={setShowResponse}
              setShowComponents={setShowComponents}
            />
          </StyledDataBases>
        ))}
    </StyledContainer>
  );
}
export default Search;

const StyledContainer = styled.div<{ $showResponse: boolean }>`
  display: flex;
  flex-direction: column;
  // justify-content: center;
  align-items: center;
  .search-input {
    //top: ${(props) => (props.$showResponse ? "20px" : "80px")};
    position: sticky;
    // margin-top: 80px;
    z-index: 5;
    top: 0;
    background: #fff;
    // padding-bottom: 10px;
  }
  height: ${(props) =>
    props.$showResponse ? "calc(100vh - 200px)" : "calc(100vh - 254px)"};
  overflow-y: auto;
  overflow-x: hidden;
  margin: 0px 28px;
  ${scrollBarStyles};
`;

const SavedDocDiv = styled.div<{ dropdownVisible: boolean }>`
  margin-top: 40px;
  border-radius: ${(props) => (props.dropdownVisible ? "30px" : "initial")};
  background: ${(props) => (props.dropdownVisible ? "#fff" : "transparent")};
  box-shadow: ${(props) =>
    props.dropdownVisible
      ? "0px 4px 4px 0px rgba(0, 0, 0, 0.25) inset"
      : "none"};
  &:hover {
    border-radius: 30px;
    background: #fff;
    box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25) inset;
  }
`;

const StyledFocusSelection = styled.div<{ $showResponse: boolean }>`
  width: ${(props) => (props.$showResponse ? "70vw" : "580px")};
  max-width: 1300px;
  // position: relative;
  position: absolute;
  top: ${(props) => (props.$showResponse ? " 163px" : "219px")};
`;

const StyledSearchContent = styled.div<{ $showResponse: boolean }>`
  position: absolute;
  // left: ${(props) => (props.$showResponse ? "-339px" : "-290px")};
  width: ${(props) => (props.$showResponse ? "70vw" : "580px")};
  max-width: 1300px;
  z-index: 4;
  top: -5px;
  padding-bottom: 10px;
  border-radius: 0 0 30px 30px;
  background: #eff1fd;
  ${(props) =>
    props.$showResponse &&
    `
  @media (min-width: 1850px) {
    top: 16px;
  }
  // @media (min-width: 3000px) {
  //   top: 43px;
  // }
  // @media (min-width: 4000px) {
  //   top: 66px;
  // }
`}
`;

// const StyledSearchType = styled.div<{ $isSavedDocDivHovered: boolean }>`
//   height: 50px;
//   opacity: 1;
//   transition: height 0.4s ease-in-out, opacity 0s ease-out;
//   transition-delay: 0s, 0.3s;
//   ${(props) =>
//     props.$isSavedDocDivHovered &&
//     css`
//       height: 0px;
//       opacity: 0;
//       transition-delay: 0s;
//     `}
// `;

const StyledDataBases = styled.div`
  // margin-top: 27px;
  margin-top: 15px;

  // &:hover {
  //   border-radius: 30px;
  //   background: #fff;
  //   box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25) inset;
  // }
`;
